import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { GoldButton } from "../components/GoldButton";
import { PERSONAS } from "../data/content";

export default function Espaces() {
  const [active, setActive] = useState(PERSONAS[0].id);
  const persona = PERSONAS.find((p) => p.id === active);

  return (
    <div data-testid="espaces-page">
      <Seo title="Espaces (propriétaire, locataire, Airbnb, technicien)" path="/espaces" description="Un parcours dédié pour chaque profil : propriétaire, locataire, location saisonnière et technicien." />
      <PageHero
        overline="Un parcours par profil"
        title={["Les espaces", "Speed Détresse"]}
        subtitle="Propriétaire, locataire, hôte Airbnb ou technicien : chaque profil dispose d'un parcours et d'outils dédiés."
        testid="espaces-hero"
      />

      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-8 lg:px-8">
        <div className="flex flex-wrap gap-3">
          {PERSONAS.map((p) => (
            <button
              key={p.id}
              data-testid={`espace-tab-${p.id}`}
              onClick={() => setActive(p.id)}
              className={`rounded-full px-6 py-3 text-sm font-medium transition-colors ${
                active === p.id
                  ? "bg-gold text-navy"
                  : "border border-white/15 text-slate-300 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={persona.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="grid items-center gap-14 lg:grid-cols-2"
            data-testid={`espace-panel-${persona.id}`}
          >
            <div>
              <Overline>{persona.label}</Overline>
              <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">{persona.title}</h2>
              <p className="mt-5 text-lg text-slate-300">{persona.intro}</p>
              <ul className="mt-8 space-y-3">
                {persona.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                      <Check className="h-3 w-3" strokeWidth={2.5} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <GoldButton to={persona.cta.to}>{persona.cta.label} <ArrowRight className="h-4 w-4" /></GoldButton>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={persona.image} alt={persona.label} className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* Commerces & pros */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="espaces-commerces">
        <Reveal><Overline>Speed Détresse parle à tout le monde</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            Agences immobilières, gestion locative, commerces, bailleurs, syndics…
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Un bac bouché par les cheveux en plein samedi après-midi, une fuite dans la cuisine d'un restaurant en plein service :
            avec ou sans abonnement, le professionnel ouvre l'application, décrit le problème, et le technicien arrive.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Reveal>
            <figure className="group overflow-hidden rounded-3xl border border-white/10 bg-navy-surface/60">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src="/images/story-salon.webp" alt="Salon de coiffure — bac de lavage bouché" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-navy">Salons de coiffure</span>
              </div>
              <figcaption className="p-6">
                <h3 className="font-serif text-xl text-white">Le bac à shampoing est bouché ?</h3>
                <p className="mt-2 text-sm text-slate-400">Avec les cheveux, ça arrive tout le temps. Une photo dans l'application et le débouchage est organisé — sans fermer le salon.</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.1}>
            <figure className="group overflow-hidden rounded-3xl border border-white/10 bg-navy-surface/60">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src="/images/story-bailleur.webp" alt="Bailleur ou syndic — tableau de bord Speed Détresse" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-navy">Bailleurs & syndics</span>
              </div>
              <figcaption className="p-6">
                <h3 className="font-serif text-xl text-white">Des dizaines de logements à suivre ?</h3>
                <p className="mt-2 text-sm text-slate-400">Chaque bien a sa mémoire numérique : interventions, états des lieux et échéances pilotés depuis un seul tableau de bord.</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="group overflow-hidden rounded-3xl border border-white/10 bg-navy-surface/60">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src="/images/story-agence-immo.webp" alt="Agence immobilière — demandes centralisées" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-navy">Agences immobilières</span>
              </div>
              <figcaption className="p-6">
                <h3 className="font-serif text-xl text-white">Toutes vos demandes d'intervention au même endroit</h3>
                <p className="mt-2 text-sm text-slate-400">Mandats, locations, ventes : l'agence fait appel à Speed Détresse pour chaque besoin et suit tout sans courir après les artisans.</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.2}>
            <figure className="group overflow-hidden rounded-3xl border border-white/10 bg-navy-surface/60">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src="/images/story-gestion-locative.webp" alt="Gestion locative — organisation des maintenances" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-navy">Agences locatives</span>
              </div>
              <figcaption className="p-6">
                <h3 className="font-serif text-xl text-white">La gestion locative sans la paperasse</h3>
                <p className="mt-2 text-sm text-slate-400">Clés, interventions, états des lieux : l'agence locative organise tout depuis l'application, même à distance.</p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="mt-8 text-center text-sm text-slate-400">
            Restaurants, bars, fast-foods, salons de coiffure, commerces, petits propriétaires, bailleurs, syndics, agences : le dépannage en un clic fonctionne pour tous.
          </p>
        </Reveal>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-28 lg:px-8">
        <Reveal>
          <div className="grid items-center gap-8 overflow-hidden rounded-3xl border border-gold/25 bg-navy-elevated md:grid-cols-[0.9fr_1.1fr]">
            <img src="/images/story-apporteur.webp" alt="Apporteur d'affaires recommandant Speed Détresse" loading="lazy" className="h-full w-full object-cover" data-testid="espaces-apporteur-img" />
            <div className="p-10 text-center md:text-left">
            <h3 className="font-serif text-3xl text-white">Apporteur d'affaires ?</h3>
            <p className="mx-auto mt-3 max-w-xl text-slate-300 md:mx-0">
              Recommandez Speed Détresse et rejoignez notre réseau de partenaires. Votre candidature devient un dossier partenaire.
            </p>
            <Link to="/rejoindre" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-gold-light">
              Devenir apporteur d'affaires <ArrowRight className="h-4 w-4" />
            </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
